import React from 'react';
import { Eye, Pencil, Plus, Package } from 'lucide-react';
import Button from '../../common/Button.js';
import {
  recomputeParcel,
  sumChargeableWeight,
  sumParcelQty,
  toNum,
} from '../../../utils/cargoParcelUtils.js';

const hasParcelData = (parcels) =>
  (parcels || []).some(
    (p) =>
      toNum(p.actual_weight) > 0 ||
      toNum(p.chargeable_weight) > 0 ||
      toNum(p.length_cm) > 0 ||
      toNum(p.width_cm) > 0 ||
      toNum(p.height_cm) > 0,
  );

/**
 * Compact package-dimension summary for Add / Edit / View shipment pages.
 * Full details open in modals to save space on mobile / iPad / desktop.
 */
const PackageDimensionSummary = ({
  parcels = [],
  mode = 'view', // 'add' | 'edit' | 'view'
  onAdd,
  onEdit,
  onView,
  onAddMore,
}) => {
  const rows = (parcels || []).map(recomputeParcel);
  const filled = hasParcelData(rows);
  const packageCount = rows.length;
  const totalQty = sumParcelQty(rows);
  const totalWeight = sumChargeableWeight(rows);
  const names = rows
    .map((r) => r.package_name)
    .filter(Boolean)
    .slice(0, 3)
    .join(', ');

  const canManage = mode === 'add' || mode === 'edit';

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4 min-w-0 mb-3 overflow-hidden">
      <div className="flex items-start gap-2.5 min-w-0">
        <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
          <Package className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm sm:text-base font-semibold text-secondary">
              Package Dimensions
            </h3>
            <span className="text-[10px] sm:text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
              Cargo
            </span>
          </div>
          {filled ? (
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5 break-words">
              {names}
              {rows.length > 3 ? ` +${rows.length - 3} more` : ''}
            </p>
          ) : (
            <p className="text-xs sm:text-sm text-gray-400 mt-0.5">
              No package dimensions added yet
            </p>
          )}
        </div>

        {/* Mobile: icon-only actions beside title */}
        <div className="flex items-center gap-1.5 shrink-0 sm:hidden">
          {mode === 'add' && !filled && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              icon={Plus}
              onClick={onAdd}
              mobileIconOnly
              mobileLabel="Add Packages"
              title="Add Packages"
            >
              Add Packages
            </Button>
          )}
          {canManage && filled && (
            <>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={Eye}
                onClick={onView}
                mobileIconOnly
                mobileLabel="View"
                title="View"
              >
                View
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={Pencil}
                onClick={onEdit}
                mobileIconOnly
                mobileLabel="Edit"
                title="Edit"
              >
                Edit
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                icon={Plus}
                onClick={onAddMore || onEdit}
                mobileIconOnly
                mobileLabel="Add Packages"
                title="Add Packages"
              >
                Add Packages
              </Button>
            </>
          )}
          {mode === 'edit' && !filled && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              icon={Plus}
              onClick={onAdd}
              mobileIconOnly
              mobileLabel="Add Packages"
              title="Add Packages"
            >
              Add Packages
            </Button>
          )}
          {mode === 'view' && filled && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              icon={Eye}
              onClick={onView}
              mobileIconOnly
              mobileLabel="View Packages"
              title="View Packages"
            >
              View Packages
            </Button>
          )}
        </div>
      </div>

      {/* Desktop / iPad: labeled buttons */}
      <div className="mt-3 hidden sm:flex sm:flex-wrap sm:justify-end sm:gap-2">
        {mode === 'add' && !filled && (
          <Button type="button" variant="primary" size="sm" icon={Plus} onClick={onAdd}>
            Add Packages
          </Button>
        )}

        {canManage && filled && (
          <>
            <Button type="button" variant="secondary" size="sm" icon={Eye} onClick={onView}>
              View
            </Button>
            <Button type="button" variant="secondary" size="sm" icon={Pencil} onClick={onEdit}>
              Edit
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              icon={Plus}
              onClick={onAddMore || onEdit}
            >
              Add Packages
            </Button>
          </>
        )}

        {mode === 'edit' && !filled && (
          <Button type="button" variant="primary" size="sm" icon={Plus} onClick={onAdd}>
            Add Packages
          </Button>
        )}

        {mode === 'view' && filled && (
          <Button type="button" variant="primary" size="sm" icon={Eye} onClick={onView}>
            View Packages
          </Button>
        )}
      </div>

      {filled && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mt-3">
          <div className="rounded-lg bg-gray-50 border border-gray-100 px-2.5 py-2 min-w-0">
            <p className="text-[10px] sm:text-xs text-gray-500 font-medium">Packages</p>
            <p className="text-sm sm:text-base font-semibold text-secondary mt-0.5">
              {packageCount}
            </p>
          </div>
          <div className="rounded-lg bg-gray-50 border border-gray-100 px-2.5 py-2 min-w-0">
            <p className="text-[10px] sm:text-xs text-gray-500 font-medium">Total Qty</p>
            <p className="text-sm sm:text-base font-semibold text-secondary mt-0.5">
              {totalQty}
            </p>
          </div>
          <div className="col-span-2 sm:col-span-1 rounded-lg bg-gray-50 border border-gray-100 px-2.5 py-2 min-w-0">
            <p className="text-[10px] sm:text-xs text-gray-500 font-medium">Chargeable Wt</p>
            <p className="text-sm sm:text-base font-semibold text-secondary mt-0.5 break-words">
              {Math.round(totalWeight)} kg
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageDimensionSummary;
